import jsPDF from 'jspdf'

function fmt(v) { return v || 'N/A' }

/**
 * Export Client Intelligence enrichment data as a branded executive PDF.
 *
 * @param {Object} params
 * @param {string} params.companyName - Client organization name
 * @param {Object} params.enrichment - AI enrichment result (companyProfile, securityPosture, etc.)
 * @param {Object} params.dns - DNS reconnaissance data
 * @param {Object} params.ssl - SSL certificate data
 * @param {Object} params.whois - WHOIS/RDAP data
 * @param {Array}  params.ctSubdomains - Certificate transparency subdomains
 * @param {Object} params.shodan - Shodan InternetDB data
 */
export function exportIntelligencePDF({ companyName, enrichment: e, dns, ssl, whois, ctSubdomains, shodan }) {
  if (!e) return

  const pdf = new jsPDF('p', 'mm', 'a4')
  const W = pdf.internal.pageSize.getWidth()
  const H = pdf.internal.pageSize.getHeight()
  const m = 20
  const cw = W - m * 2
  let y = m

  const DARK = [1, 36, 56]
  const ACCENT = [138, 208, 214]
  const GRAY = [100, 108, 124]
  const LIGHT_GRAY = [160, 168, 176]

  const checkPage = (need) => { if (y + need > H - m) { pdf.addPage(); y = m; return true } return false }

  const sectionTitle = (title) => {
    checkPage(16)
    pdf.setFontSize(11); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(...DARK)
    pdf.text(title.toUpperCase(), m, y); y += 2
    pdf.setDrawColor(...ACCENT); pdf.setLineWidth(0.8); pdf.line(m, y, m + 30, y); y += 6
  }

  const bulletList = (items, color = GRAY) => {
    (items || []).forEach(item => {
      checkPage(10)
      pdf.setFillColor(...color); pdf.circle(m + 2, y - 1, 1, 'F')
      pdf.setFontSize(8.5); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(...DARK)
      const lines = pdf.splitTextToSize(String(item), cw - 10)
      pdf.text(lines, m + 6, y); y += lines.length * 4.5 + 1
    })
  }

  // ── Cover Page ──
  pdf.setFillColor(...DARK); pdf.rect(0, 0, W, H, 'F')

  pdf.setFillColor(...ACCENT); pdf.roundedRect(W / 2 - 12, 50, 24, 24, 4, 4, 'F')
  pdf.setFontSize(14); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(...DARK)
  pdf.text('SSA', W / 2, 66, { align: 'center' })

  pdf.setFontSize(28); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(255, 255, 255)
  pdf.text('Client Intelligence', W / 2, 100, { align: 'center' })
  pdf.setFontSize(14); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(...ACCENT)
  pdf.text('Executive Briefing', W / 2, 110, { align: 'center' })

  pdf.setFontSize(20); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(255, 255, 255)
  pdf.text(companyName, W / 2, 140, { align: 'center' })

  pdf.setFontSize(11); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(...LIGHT_GRAY)
  const subtitle = [e.companyProfile?.industry, e.companyProfile?.subIndustry].filter(Boolean).join(' / ')
  if (subtitle) pdf.text(subtitle, W / 2, 150, { align: 'center' })
  if (e.companyProfile?.headquarters) pdf.text(e.companyProfile.headquarters, W / 2, 157, { align: 'center' })

  pdf.setFontSize(9); pdf.setTextColor(...LIGHT_GRAY)
  pdf.text(`Prepared ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`, W / 2, H - 40, { align: 'center' })
  pdf.text('Sidewalk Security Advisors', W / 2, H - 34, { align: 'center' })

  if (e.securityPosture?.overallGrade) {
    const gradeColors = { A: [34, 197, 94], B: [34, 197, 94], C: [245, 158, 11], D: [249, 115, 22], F: [239, 68, 68] }
    const gc = gradeColors[e.securityPosture.overallGrade] || LIGHT_GRAY
    pdf.setFontSize(9); pdf.setTextColor(...LIGHT_GRAY); pdf.text('External Security Grade', W / 2, 175, { align: 'center' })
    pdf.setFillColor(...gc); pdf.roundedRect(W / 2 - 14, 180, 28, 28, 4, 4, 'F')
    pdf.setFontSize(22); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(255, 255, 255)
    pdf.text(e.securityPosture.overallGrade, W / 2, 198, { align: 'center' })
  }

  if (e.confidence) {
    pdf.setFontSize(8); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(...LIGHT_GRAY)
    pdf.text(`Confidence: ${e.confidence.overall}`, W / 2, H - 24, { align: 'center' })
  }

  // ── Page 2: Company Profile + Technology ──
  pdf.addPage(); y = m

  sectionTitle('Company Profile')
  if (e.companyProfile?.description) {
    pdf.setFontSize(9); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(...GRAY)
    const descLines = pdf.splitTextToSize(e.companyProfile.description, cw)
    pdf.text(descLines, m, y); y += descLines.length * 4.5 + 4
  }

  const profileItems = [
    ['Industry', e.companyProfile?.industry], ['Sub-Industry', e.companyProfile?.subIndustry],
    ['Business Model', e.companyProfile?.businessModel], ['Est. Size', e.companyProfile?.estimatedSize],
    ['Est. Revenue', e.companyProfile?.estimatedRevenue], ['Headquarters', e.companyProfile?.headquarters],
    ['Years in Operation', e.companyProfile?.yearsInOperation], ['Domain Age', whois?.domainAge != null ? `${whois.domainAge} years` : null]
  ]
  const halfW = cw / 2 - 4

  const renderCell = (label, value, xOff, maxW) => {
    pdf.setFontSize(7.5); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(...LIGHT_GRAY)
    pdf.text(label, m + xOff, y)
    pdf.setFontSize(9); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(...DARK)
    const valLines = pdf.splitTextToSize(fmt(value), maxW)
    pdf.text(valLines, m + xOff, y + 4)
    return 8 + valLines.length * 4
  }

  for (let i = 0; i < profileItems.length; i += 2) {
    checkPage(20)
    const leftH = renderCell(profileItems[i][0], profileItems[i][1], 0, halfW)
    let rightH = 0
    if (profileItems[i + 1]) {
      rightH = renderCell(profileItems[i + 1][0], profileItems[i + 1][1], halfW + 8, halfW)
    }
    y += Math.max(leftH, rightH) + 2
  }
  y += 6

  // Leadership (if available)
  if (e.companyProfile?.leadership?.length > 0) {
    sectionTitle(`Leadership (${e.companyProfile.leadership.length} identified)`)
    const leaders = e.companyProfile.leadership
    const leadHalfW = cw / 2 - 4
    for (let i = 0; i < leaders.length; i += 2) {
      checkPage(10)
      const startY = y
      // Left column
      pdf.setFontSize(9); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(...DARK)
      pdf.text(leaders[i].name || '—', m, y)
      pdf.setFontSize(8); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(...GRAY)
      pdf.text(leaders[i].title || '', m, y + 4)
      // Right column
      if (leaders[i + 1]) {
        pdf.setFontSize(9); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(...DARK)
        pdf.text(leaders[i + 1].name || '—', m + leadHalfW + 8, y)
        pdf.setFontSize(8); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(...GRAY)
        pdf.text(leaders[i + 1].title || '', m + leadHalfW + 8, y + 4)
      }
      y = startY + 10
    }
    y += 2
    pdf.setFontSize(7); pdf.setFont('helvetica', 'italic'); pdf.setTextColor(...LIGHT_GRAY)
    pdf.text('Contact information not publicly disclosed. Obtain directly during engagement.', m, y)
    y += 8
  }

  sectionTitle('Technology Profile')
  const techItems = [
    ['Email Platform', e.technologyProfile?.emailPlatform], ['Cloud Platform', e.technologyProfile?.cloudPlatform],
    ['Identity Platform', e.technologyProfile?.identityPlatform], ['DNS Provider', e.technologyProfile?.dnsProvider || dns?.dnsProvider],
    ['Hosting', e.technologyProfile?.hostingProvider], ['Website', e.technologyProfile?.websiteTechnology]
  ]
  const labelColW = 45
  const valColW = cw - labelColW
  techItems.forEach(item => {
    checkPage(12)
    pdf.setFontSize(8.5); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(...LIGHT_GRAY)
    pdf.text(item[0], m, y)
    pdf.setTextColor(...DARK)
    const valLines = pdf.splitTextToSize(fmt(item[1]), valColW)
    pdf.text(valLines, m + labelColW, y)
    y += valLines.length * 4.2 + 2
  })

  const detectedServices = [...new Set([...(e.technologyProfile?.detectedServices || []), ...(dns?.detectedServices || [])])]
  if (detectedServices.length > 0) {
    y += 3
    pdf.setFontSize(7.5); pdf.setTextColor(...LIGHT_GRAY); pdf.text('Detected Services:', m, y); y += 4
    pdf.setFontSize(8.5); pdf.setTextColor(...DARK)
    const svcLines = pdf.splitTextToSize(detectedServices.join(', '), cw)
    pdf.text(svcLines, m, y); y += svcLines.length * 4.2 + 2
  }

  // Regulatory Profile
  y += 4
  sectionTitle('Regulatory Profile')
  const frameworks = e.regulatoryProfile?.applicableFrameworks || []
  if (frameworks.length > 0) {
    pdf.setFontSize(8.5); pdf.setTextColor(...DARK)
    const fwText = pdf.splitTextToSize(frameworks.join('  |  '), cw)
    pdf.text(fwText, m, y); y += fwText.length * 4.2 + 3
  }
  const regFlags = [
    ['Publicly Traded', e.regulatoryProfile?.publiclyTraded],
    ['Government Contractor', e.regulatoryProfile?.governmentContractor],
    ['Processes Payments', e.regulatoryProfile?.processesPayments],
    ['Handles Health Data', e.regulatoryProfile?.handlesHealthData],
    ['International Operations', e.regulatoryProfile?.operatesInternationally]
  ]
  regFlags.filter(([, val]) => val).forEach(([label]) => {
    checkPage(6)
    pdf.setFillColor(34, 197, 94); pdf.circle(m + 2, y - 1, 1.2, 'F')
    pdf.setFontSize(8.5); pdf.setTextColor(...DARK); pdf.text(label, m + 6, y); y += 5
  })

  if (e.regulatoryProfile?.dataTypes?.length > 0) {
    y += 2
    pdf.setFontSize(7.5); pdf.setTextColor(...LIGHT_GRAY); pdf.text('Sensitive Data Types:', m, y); y += 4
    pdf.setFontSize(8.5); pdf.setTextColor(...DARK)
    const dtText = pdf.splitTextToSize(e.regulatoryProfile.dataTypes.join(', '), cw)
    pdf.text(dtText, m, y); y += dtText.length * 4.5
  }

  // ── Page 3: Security Posture ──
  pdf.addPage(); y = m

  sectionTitle('Security Posture')
  if (e.securityPosture?.overallGradeRationale) {
    pdf.setFontSize(9); pdf.setTextColor(...GRAY)
    const rationale = pdf.splitTextToSize(e.securityPosture.overallGradeRationale, cw)
    pdf.text(rationale, m, y); y += rationale.length * 4.5 + 4
  }

  const grades = [
    ['Email Security', e.securityPosture?.emailSecurity?.grade, e.securityPosture?.emailSecurity?.notes],
    ['Certificate Security', e.securityPosture?.certificateSecurity?.grade, e.securityPosture?.certificateSecurity?.notes],
    ['DNS Security', e.securityPosture?.dnsSecurity?.grade, e.securityPosture?.dnsSecurity?.notes]
  ]
  grades.forEach(([label, grade, notes]) => {
    checkPage(14)
    pdf.setFontSize(9); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(...DARK)
    pdf.text(`${label}: ${grade || '—'}`, m, y); y += 4
    if (notes) {
      pdf.setFontSize(8); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(...GRAY)
      const noteLines = pdf.splitTextToSize(notes, cw)
      pdf.text(noteLines, m, y); y += noteLines.length * 4 + 3
    }
  })

  y += 2
  const emailDetails = [
    ['SPF', dns?.hasSPF ? 'Present' : 'Missing'],
    ['DMARC', dns?.hasDMARC ? (dns.dmarcPolicy || 'Present') : 'Missing'],
    ['DKIM', dns?.hasDKIM ? 'Present' : 'Not detected'],
    ['CAA', dns?.hasCAA ? 'Present' : 'Missing']
  ]
  emailDetails.forEach(([label, val]) => {
    checkPage(6)
    pdf.setFontSize(8.5); pdf.setTextColor(...LIGHT_GRAY); pdf.text(label, m, y)
    const isGood = val === 'Present' || val === 'reject' || val === 'quarantine'
    pdf.setTextColor(isGood ? 34 : 200, isGood ? 150 : 80, isGood ? 80 : 80)
    pdf.text(val, m + 30, y); y += 5
  })

  if (ssl && !ssl.error) {
    y += 4
    checkPage(20)
    pdf.setFontSize(8.5); pdf.setTextColor(...LIGHT_GRAY); pdf.text('Certificate:', m, y)
    pdf.setTextColor(...DARK)
    const certText = pdf.splitTextToSize(`${ssl.issuer} (${ssl.certType}) — ${ssl.isExpired ? 'EXPIRED' : `${ssl.daysUntilExpiry} days remaining`}`, cw - 30)
    pdf.text(certText, m + 30, y)
    y += certText.length * 4.2 + 2
  }

  // External Attack Surface
  const as = e.securityPosture?.attackSurface
  const ctSubs = ctSubdomains || []
  const shodanData = shodan || {}
  if (as || ctSubs.length > 0 || shodanData.ports?.length > 0) {
    y += 4
    sectionTitle('External Attack Surface')
    const totalSubs = ctSubs.length || ssl?.sans?.length || 0
    if (totalSubs > 0) {
      pdf.setFontSize(8); pdf.setTextColor(...GRAY)
      pdf.text(`${totalSubs} subdomain${totalSubs !== 1 ? 's' : ''} discovered via certificate transparency and SSL`, m, y); y += 5
    }

    if (as?.notableSubdomains?.length > 0) {
      checkPage(10)
      pdf.setFontSize(7.5); pdf.setTextColor(...LIGHT_GRAY); pdf.text('Notable Subdomains:', m, y); y += 4
      pdf.setFontSize(8); pdf.setTextColor(...DARK)
      const subText = pdf.splitTextToSize(as.notableSubdomains.join(', '), cw)
      pdf.text(subText, m, y); y += subText.length * 4 + 2
    }

    if (shodanData.ports?.length > 0) {
      checkPage(10)
      pdf.setFontSize(7.5); pdf.setTextColor(...LIGHT_GRAY)
      pdf.text(`Open Ports (${shodanData.ip || 'primary IP'}):`, m, y); y += 4
      pdf.setFontSize(8); pdf.setTextColor(...DARK)
      pdf.text(shodanData.ports.join(', '), m, y); y += 5
      if (as?.openPorts?.length > 0) {
        as.openPorts.forEach(note => {
          checkPage(8)
          pdf.setFontSize(7.5); pdf.setTextColor(...GRAY)
          const noteLines = pdf.splitTextToSize(`- ${note}`, cw - 4)
          pdf.text(noteLines, m + 2, y); y += noteLines.length * 3.8 + 1
        })
      }
    }

    if (shodanData.vulns?.length > 0) {
      checkPage(10)
      pdf.setFontSize(7.5); pdf.setTextColor(...LIGHT_GRAY); pdf.text('Known Vulnerabilities:', m, y); y += 4
      pdf.setFontSize(8); pdf.setTextColor(239, 68, 68)
      pdf.text(shodanData.vulns.slice(0, 10).join(', '), m, y); y += 5
    }

    if (as?.notes) {
      checkPage(8)
      pdf.setFontSize(8); pdf.setTextColor(...GRAY)
      const noteLines = pdf.splitTextToSize(as.notes, cw)
      pdf.text(noteLines, m, y); y += noteLines.length * 4 + 2
    }
  }

  // Observations + Quick Wins
  const bulletListStructured = (items, color, textKey, whyKey) => {
    (items || []).forEach(item => {
      const text = typeof item === 'string' ? item : item[textKey]
      const why = typeof item === 'string' ? null : item[whyKey]
      const wtd = typeof item === 'string' ? null : item.whatToDo
      checkPage(why ? 24 : 10)
      pdf.setFillColor(...color); pdf.circle(m + 2, y - 1, 1, 'F')
      pdf.setFontSize(8.5); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(...DARK)
      const lines = pdf.splitTextToSize(String(text), cw - 10)
      pdf.text(lines, m + 6, y); y += lines.length * 4.5
      if (why) {
        pdf.setFontSize(7.5); pdf.setFont('helvetica', 'italic'); pdf.setTextColor(...GRAY)
        const whyLines = pdf.splitTextToSize(`Why it matters: ${why}`, cw - 10)
        pdf.text(whyLines, m + 6, y); y += whyLines.length * 3.8
      }
      if (wtd) {
        pdf.setFontSize(7.5); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(...GRAY)
        const wtdLines = pdf.splitTextToSize(`What to do: ${wtd}`, cw - 10)
        pdf.text(wtdLines, m + 6, y); y += wtdLines.length * 3.8
      }
      y += 3
    })
  }

  y += 4
  sectionTitle('Security Observations')
  bulletListStructured(e.securityPosture?.initialObservations, [245, 158, 11], 'observation', 'whyItMatters')

  y += 4
  sectionTitle('Quick Wins')
  ;(e.securityPosture?.quickWins || []).forEach(item => {
    const text = typeof item === 'string' ? item : item.action
    const wtd = typeof item === 'string' ? null : item.whatToDo
    const loe = typeof item === 'string' ? null : item.levelOfEffort
    checkPage(wtd ? 20 : 10)
    pdf.setFillColor(34, 197, 94); pdf.circle(m + 2, y - 1, 1, 'F')
    pdf.setFontSize(8.5); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(...DARK)
    const lines = pdf.splitTextToSize(String(text), cw - (loe ? 35 : 10))
    pdf.text(lines, m + 6, y)
    if (loe) {
      pdf.setFontSize(7); pdf.setFont('helvetica', 'normal')
      const isLow = loe.toLowerCase().startsWith('low')
      const isMed = loe.toLowerCase().startsWith('med')
      pdf.setTextColor(isLow ? 34 : isMed ? 180 : 200, isLow ? 150 : isMed ? 120 : 60, isLow ? 80 : isMed ? 0 : 60)
      pdf.text(`Effort: ${loe}`, W - m, y, { align: 'right' })
    }
    y += lines.length * 4.5
    if (wtd) {
      pdf.setFontSize(7.5); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(...GRAY)
      const wtdLines = pdf.splitTextToSize(`What to do: ${wtd}`, cw - 10)
      pdf.text(wtdLines, m + 6, y); y += wtdLines.length * 3.8
    }
    y += 3
  })

  if (e.securityPosture?.suggestedCriticalSystems?.length > 0) {
    y += 4
    sectionTitle('Suggested Critical Systems')
    e.securityPosture.suggestedCriticalSystems.forEach((sys, i) => {
      checkPage(6)
      pdf.setFontSize(8.5); pdf.setTextColor(...LIGHT_GRAY); pdf.text(`${i + 1}.`, m, y)
      pdf.setTextColor(...DARK); pdf.text(sys, m + 8, y); y += 5
    })
  }

  // ── Page 4: Engagement Intelligence ──
  pdf.addPage(); y = m

  sectionTitle('Engagement Intelligence')

  if (e.engagementContext?.maturityEstimate) {
    checkPage(16)
    pdf.setFontSize(9); pdf.setTextColor(...LIGHT_GRAY); pdf.text('Estimated Maturity Level', m, y); y += 5
    pdf.setFontSize(16); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(...DARK)
    pdf.text(e.engagementContext.maturityEstimate, m, y); y += 5
    if (e.engagementContext.maturityRationale) {
      pdf.setFontSize(8.5); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(...GRAY)
      const rl = pdf.splitTextToSize(e.engagementContext.maturityRationale, cw)
      pdf.text(rl, m, y); y += rl.length * 4.5
    }
    y += 4
  }

  if (e.engagementContext?.threatProfileRecommendation) {
    checkPage(14)
    pdf.setFontSize(8.5); pdf.setTextColor(...LIGHT_GRAY); pdf.text('Recommended Threat Profile:', m, y); y += 4
    pdf.setFontSize(9); pdf.setTextColor(...DARK)
    const tpLines = pdf.splitTextToSize(e.engagementContext.threatProfileRecommendation, cw)
    pdf.text(tpLines, m, y); y += tpLines.length * 4.5 + 4
  }

  sectionTitle('Key Risks')
  bulletList(e.engagementContext?.keyRisks, [239, 68, 68])

  y += 4
  sectionTitle('Suggested Focus Areas')
  bulletList(e.engagementContext?.suggestedFocusAreas, [59, 130, 246])

  if (e.engagementContext?.conversationStarters?.length > 0) {
    y += 4
    sectionTitle('First Meeting Conversation Starters')
    e.engagementContext.conversationStarters.forEach((q, i) => {
      checkPage(12)
      pdf.setFontSize(8.5); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(...ACCENT)
      pdf.text(`${i + 1}.`, m, y)
      pdf.setFont('helvetica', 'normal'); pdf.setTextColor(...DARK)
      const ql = pdf.splitTextToSize(q, cw - 8)
      pdf.text(ql, m + 7, y); y += ql.length * 4.5 + 2
    })
  }

  // ── Appendix: Methodology & Data Sources ──
  pdf.addPage(); y = m
  sectionTitle('Appendix A: Methodology & Data Sources')

  pdf.setFontSize(9); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(...GRAY)
  const methodologyText = 'This report is derived from passive reconnaissance against publicly available data sources. No active scanning, vulnerability probing, credential testing, or exploit validation was performed. The analysis represents a point-in-time snapshot based on data observable without authenticated access to client systems.'
  const methodLines = pdf.splitTextToSize(methodologyText, cw)
  pdf.text(methodLines, m, y); y += methodLines.length * 4.5 + 6

  pdf.setFontSize(8.5); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(...DARK)
  pdf.text('Data Sources Used', m, y); y += 5

  const dataSources = [
    ['Certificate Transparency Logs', 'crt.sh', 'Historical SSL/TLS certificate records for subdomain discovery.'],
    ['Passive DNS', 'HackerTarget hostsearch', 'Historical DNS records and hostname associations.'],
    ['Public Internet Census', 'Shodan InternetDB', 'Open ports, detected software (CPE), and known vulnerabilities (CVE) observed on public IPs.'],
    ['Domain Registration', 'RDAP / WHOIS via rdap.org', 'Registrar, registration and expiration dates, registrant information where public.'],
    ['DNS Intelligence', 'Google Public DNS (DoH) and Google DNS over HTTPS API', 'MX, TXT, NS, A, CAA, and DKIM record lookups for email authentication and infrastructure analysis.'],
    ['SSL/TLS Certificate Inspection', 'Direct TLS handshake', 'Issuer, certificate type (DV/OV/EV), expiration, and Subject Alternative Names. Available in local analysis only.'],
    ['Public Website Content', 'HTTPS GET to primary domain', 'One request to the homepage for context extraction. No crawling or path enumeration performed.'],
    ['AI Analysis', 'Anthropic Claude Sonnet', 'Synthesis of collected signals into the business, technology, security, and engagement assessments presented in this report.']
  ]

  dataSources.forEach(([name, source, description]) => {
    checkPage(14)
    pdf.setFontSize(8); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(...DARK)
    pdf.text(name, m, y); y += 3.5
    pdf.setFontSize(7.5); pdf.setFont('helvetica', 'italic'); pdf.setTextColor(...LIGHT_GRAY)
    pdf.text(`Source: ${source}`, m, y); y += 3.5
    pdf.setFontSize(8); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(...GRAY)
    const descLines = pdf.splitTextToSize(description, cw)
    pdf.text(descLines, m, y); y += descLines.length * 3.8 + 3
  })

  y += 4
  pdf.setFontSize(8.5); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(...DARK)
  pdf.text('Out of Scope', m, y); y += 5
  pdf.setFontSize(8); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(...GRAY)
  const oosText = 'The following activities were explicitly not performed as part of this passive reconnaissance: active port or vulnerability scanning, fuzzing or directory brute forcing, exploit validation, credential testing or password spraying, social engineering, and any activity requiring authenticated access to client systems.'
  const oosLines = pdf.splitTextToSize(oosText, cw)
  pdf.text(oosLines, m, y); y += oosLines.length * 4 + 4

  pdf.setFontSize(8.5); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(...DARK)
  pdf.text('Interpreting Findings', m, y); y += 5
  pdf.setFontSize(8); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(...GRAY)
  const interpText = 'Findings should be treated as observed indicators rather than confirmed vulnerabilities. Public data sources may be incomplete, out of date, or incorrectly attributed. Verify findings through authenticated review before making operational or investment decisions.'
  const interpLines = pdf.splitTextToSize(interpText, cw)
  pdf.text(interpLines, m, y); y += interpLines.length * 4 + 2

  // ── Footer on all pages ──
  const pageCount = pdf.internal.getNumberOfPages()
  const footerLineY = H - 16
  const footerTextY = H - 12
  const footerMaxW = cw / 2 - 10

  for (let i = 2; i <= pageCount; i++) {
    pdf.setPage(i)
    pdf.setDrawColor(...LIGHT_GRAY); pdf.setLineWidth(0.2); pdf.line(m, footerLineY, W - m, footerLineY)
    pdf.setFontSize(7); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(...LIGHT_GRAY)
    const footerLeft = pdf.splitTextToSize(`${companyName}  |  Client Intelligence Briefing`, footerMaxW)
    pdf.text(footerLeft, m, footerTextY)
    pdf.text(`Page ${i - 1} of ${pageCount - 1}`, W - m, footerTextY, { align: 'right' })
  }

  // Confidence disclaimer on last page
  pdf.setPage(pageCount)
  const disclaimerY = H - 30
  pdf.setFontSize(7); pdf.setTextColor(...LIGHT_GRAY)
  const confText = `Confidence: ${e.confidence?.overall || 'N/A'}. ${e.confidence?.notes || 'Based on publicly available information.'}`
  const confLines = pdf.splitTextToSize(confText, cw)
  pdf.text(confLines, m, disclaimerY)
  const discY = disclaimerY + confLines.length * 3.5
  const discLines = pdf.splitTextToSize('This report contains AI-generated analysis based on publicly available data. Verify findings before making business decisions.', cw)
  pdf.text(discLines, m, discY)

  pdf.save(`${companyName.replace(/[^a-zA-Z0-9]/g, '_')}_Intelligence_Briefing.pdf`)
}
